<template>
  <teleport :to="teleportTarget">
    <div class="toast-container">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="['toast-item', `toast-${toast.type}`]"
          @click="removeToast(toast.id)"
        >
          <div class="toast-icon">
            <i :class="toast.category ? ['bi', toast.category.icon] : getIcon(toast.type)"></i>
          </div>
          <div class="toast-content">
            <div v-if="toast.category" class="toast-category">{{ toast.category.label }}</div>
            <div v-if="toast.title" class="toast-title">{{ toast.title }}</div>
            <div class="toast-message" :class="{ clamped: toast.notificationId }">{{ toast.message }}</div>
            <button
              v-if="toast.action"
              type="button"
              class="toast-action"
              @click.stop="runAction(toast)"
            >
              {{ toast.action.label }} <i class="bi bi-arrow-right-short"></i>
            </button>
          </div>
          <button class="toast-close" @click.stop="removeToast(toast.id)">
            <i class="bi bi-x"></i>
          </button>
          <div
            v-if="toast.duration > 0 && !toast.persistent"
            class="toast-progress"
            :style="{ animationDuration: `${toast.duration}ms` }"
          ></div>
        </div>
      </TransitionGroup>
    </div>
  </teleport>
</template>

<script>
import { useNotifications } from '../composables/useNotifications'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

export default {
  name: 'ToastNotification',
  setup() {
    const { toasts, removeToast, markAsRead } = useNotifications()
    const router = useRouter()

    // Bouton d'un toast de notification : ouvre la page concernée et marque la notification lue
    const runAction = (toast) => {
      if (toast.notificationId) markAsRead(toast.notificationId)
      removeToast(toast.id)
      router.push(toast.action.to).catch(() => {})
    }
    const isFullscreen = ref(false)

    const teleportTarget = computed(() => {
      if (isFullscreen.value && document.fullscreenElement) {
        return document.fullscreenElement
      }
      return 'body'
    })

    const onFullscreenChange = () => {
      isFullscreen.value = !!document.fullscreenElement
    }

    onMounted(() => {
      document.addEventListener('fullscreenchange', onFullscreenChange)
      document.addEventListener('webkitfullscreenchange', onFullscreenChange)
      document.addEventListener('mozfullscreenchange', onFullscreenChange)
      document.addEventListener('MSFullscreenChange', onFullscreenChange)
    })

    onUnmounted(() => {
      document.removeEventListener('fullscreenchange', onFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange)
      document.removeEventListener('mozfullscreenchange', onFullscreenChange)
      document.removeEventListener('MSFullscreenChange', onFullscreenChange)
    })

    const getIcon = (type) => {
      const icons = {
        success: 'bi bi-check-circle-fill',
        error: 'bi bi-exclamation-circle-fill',
        warning: 'bi bi-exclamation-triangle-fill',
        info: 'bi bi-info-circle-fill'
      }
      return icons[type] || icons.info
    }

    return {
      toasts,
      removeToast,
      getIcon,
      runAction,
      teleportTarget
    }
  }
}
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 80px;
  right: 20px;
  z-index: 20000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
}

.toast-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 320px;
  max-width: 420px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
  pointer-events: auto;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.toast-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
}

.toast-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  color: var(--text-main);
}

.toast-message {
  font-size: 14px;
  color: var(--text-muted);
  line-height: 1.4;
  word-wrap: break-word;
}

.toast-category {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin-bottom: 2px;
}

.toast-message.clamped {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-line;
}

.toast-action {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 8px;
  padding: 4px 10px;
  border: 1px solid currentColor;
  border-radius: 8px;
  background: transparent;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--primary-color, #4f46e5);
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.toast-action:hover {
  background: rgba(79, 70, 229, 0.08);
}

.toast-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  border-radius: 6px;
  transition: background-color 0.2s ease, color 0.2s ease;
  padding: 0;
}

.toast-close:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: var(--text-main);
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  background: currentColor;
  opacity: 0.3;
  transform-origin: left;
  animation: progress linear forwards;
}

@keyframes progress {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

/* Toast type colors */
.toast-success {
  border-left: 4px solid #10b981;
}

.toast-success .toast-icon {
  color: #10b981;
}

.toast-success .toast-progress {
  color: #10b981;
}

.toast-error {
  border-left: 4px solid #ef4444;
}

.toast-error .toast-icon {
  color: #ef4444;
}

.toast-error .toast-progress {
  color: #ef4444;
}

.toast-warning {
  border-left: 4px solid #f59e0b;
}

.toast-warning .toast-icon {
  color: #f59e0b;
}

.toast-warning .toast-progress {
  color: #f59e0b;
}

.toast-info {
  border-left: 4px solid #3b82f6;
}

.toast-info .toast-icon {
  color: #3b82f6;
}

.toast-info .toast-progress {
  color: #3b82f6;
}

/* Transitions */
.toast-enter-active {
  animation: slideInRight 0.3s ease-out;
}

.toast-leave-active {
  animation: slideOutRight 0.3s ease-in;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOutRight {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .toast-container {
    top: 60px;
    right: 12px;
    left: 12px;
  }

  .toast-item {
    min-width: auto;
    max-width: none;
  }
}
</style>
